import { useQueryClient } from "@tanstack/react-query";
import { Slot, VisuallyHidden } from "radix-ui";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { ROUTES } from "@/app/router";
import { triggerCustomerIoProductClicked } from "@/features/analytics/triggers";
import { productsQueriesStore } from "@/features/products/api";
import {
  CompactProductCard,
  ExpandedProductCard,
} from "@/features/products/components/shop-overview";
import { useShopContext } from "@/features/products/context";
import type { SelectedProduct } from "@/features/products/context";
import { Dialog } from "@/shared/ui";

interface ProductCardModalProps {
  product: SelectedProduct;
}

export const ProductDetailsModal = ({ product }: ProductCardModalProps) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const {
    actions: { selectProduct },
  } = useShopContext();

  const handleContinue = () => {
    selectProduct(product);
    navigate(ROUTES.SHOP.SUMMARY);
  };

  const handleHoverContinue = () =>
    queryClient.ensureQueryData(
      productsQueriesStore.products.shop._ctx.discounts(product.id),
    );

  const isProductDisabled = Boolean(product.disabled?.length);
  const CompactProductCardWrapper = isProductDisabled
    ? Slot.Root
    : Dialog.Trigger;

  if (!product) {
    return null;
  }

  return (
    <Dialog.Root>
      <CompactProductCardWrapper
        asChild={isProductDisabled}
        onClick={() => triggerCustomerIoProductClicked({ product })}
      >
        <CompactProductCard product={product} disabled={isProductDisabled} />
      </CompactProductCardWrapper>

      <Dialog.Content withCloseButton={false} size="large">
        <VisuallyHidden.Root>
          <Dialog.Title>{t`Product details for ${product.name}.`}</Dialog.Title>
          <Dialog.Description>
            {t`Clicking "Select" will take you to the summary page to review your selection.`}
          </Dialog.Description>
        </VisuallyHidden.Root>

        <ExpandedProductCard
          product={product}
          onContinue={handleContinue}
          onHoverContinue={handleHoverContinue}
        />
      </Dialog.Content>
    </Dialog.Root>
  );
};
